import dayjs from 'dayjs';
import { useStoreState } from 'easy-peasy';
import { useMemo, useRef } from 'react';
import { useQuery } from 'react-query';

import { AlarmType, AssetConnectionStatus } from '@ge/models/constants';
import { AssetType, QueryKey } from '@ge/models/constants';
import { SortValueType } from '@ge/models/constants';
import { sorter } from '@ge/util/metric-sorter';

import { fetchRealTimeDataForAsset } from '../services/asset';
import { fetchAssetsDataAssignments } from '../services/asset';
import { getEventManagementList } from '../services/event-management';
// TODO: pull this from config, ideally something that can be updated without a redeploy
const REAL_TIME_DATA_REFRESH = 30000;

const mockAlarms = [
  {
    code: '2',
    severity: 'high',
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt',
    eventClass: 'Information',
    start: dayjs().subtract(151, 'minutes').toISOString(),
  },
  {
    code: '10000020',
    severity: 'medium',
    description: 'No Errors',
    eventClass: 'Information',
    start: dayjs().subtract(93, 'minutes').toISOString(),
  },
  {
    code: 'weca-21-f324-ee334-44433',
    severity: 'low',
    description: 'Lorem ipsum dolor sit amet',
    eventClass: 'Information',
    start: dayjs().subtract(1, 'hours').toISOString(),
  },
];

export const useAssetRealTimeData = ({ assetId, sortDirection, sortMetric, groupEvents }) => {
  const { isLoading: isAssetsLoading } = useStoreState((state) => state.assets);
  const getSiteById = useStoreState((state) => state.sites.getSiteById);
  const getAssetById = useStoreState((state) => state.assets.getAssetById);
  const asset = getAssetById(assetId);

  const prevAssetIdRef = useRef(assetId);
  const assetChanged = prevAssetIdRef.current !== assetId;
  prevAssetIdRef.current = assetId;

  const queryKey =
    groupEvents !== undefined
      ? [QueryKey.ASSET_REALTIME_DATA, assetId, groupEvents]
      : [QueryKey.ASSET_REALTIME_DATA, assetId];

  const {
    data: rtData,
    error,
    isLoading,
    dataUpdatedAt,
    isFetching,
  } = useQuery(
    queryKey,
    async () => fetchRealTimeDataForAsset(asset, groupEvents !== undefined ? { groupEvents } : {}),
    {
      // TODO enable for all asset types
      enabled: Boolean(assetId) && Boolean(asset),
      refetchInterval: REAL_TIME_DATA_REFRESH,
      // https://github.com/tannerlinsley/react-query/discussions/1118#discussioncomment-90017
      refetchOnWindowFocus: true,
      keepPreviousData: !assetChanged,
    },
  );

  const { data: assetDataAssignments } = useQuery(
    [QueryKey.ASSET_REALTIME_DATA, assetId, 'data-assignment'],
    () => fetchAssetsDataAssignments([assetId], 'realtime'),
    {
      enabled: Boolean(assetId),
      refetchInterval: REAL_TIME_DATA_REFRESH,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
    },
  );

  const resolvedEventMapId =
    rtData?.eventMapId ||
    assetDataAssignments?.[0]?.eventMapId ||
    assetDataAssignments?.[0]?.signalMapId;

  const { data: eventManagementData } = useQuery(
    [
      QueryKey.EVENT_MANAGEMENT_LIST,
      resolvedEventMapId,
      'asset-rt-ack-reset',
    ],
    async () => {
      if (resolvedEventMapId) {
        return getEventManagementList({ mapId: resolvedEventMapId, blockAckReset: true });
      }
      return [];
    },
    {
      enabled: Boolean(resolvedEventMapId),
      staleTime: REAL_TIME_DATA_REFRESH,
      refetchInterval: REAL_TIME_DATA_REFRESH,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return useMemo(() => {
    const site = getSiteById(asset?.site?.id);
    let metrics;
    let alarms;
    const ackResetLookup = new Map();

    eventManagementData?.forEach((eventMap) => {
      eventMap?.events?.forEach((event) => {
        const blockAckReset = event?.commandProperties?.blockAckReset === true;

        [event?.eventId, event?.sourceEventId, event?.code]
          .filter((value) => value !== undefined && value !== null && value !== '')
          .forEach((value) => {
            ackResetLookup.set(String(value), blockAckReset);
          });
      });
    });

    if (
      asset?.type === AssetType.WIND_TURBINE ||
      asset?.type === AssetType.SITE_CONTROLLER ||
      asset?.type === AssetType.STORAGE_INVERTER ||
      asset?.type === AssetType.SOLAR_INVERTER
    ) {
      metrics = rtData?.metrics;
      alarms = rtData?.events;
    } else if (asset?.type === AssetType.SUBSTATION) {
      metrics = rtData?.metrics;
      alarms = rtData?.events;
    } else {
      // TODO remove mocks for other asset types
      metrics = { conditions: { wind: null, temp: null }, readings: { signals: [] } };
      alarms = mockAlarms;
    }

    alarms = Array.isArray(alarms)
      ? alarms.map((alarm) => ({
          ...alarm,
          commandProperties: {
            ...(alarm?.commandProperties || {}),
            blockAckReset:
              alarm?.commandProperties?.blockAckReset ??
              ackResetLookup.get(String(alarm?.eventId)) ??
              ackResetLookup.get(String(alarm?.sourceEventId)) ??
              ackResetLookup.get(String(alarm?.code)) ??
              false,
          },
          duration: alarm?.start ? dayjs().diff(dayjs(alarm?.start), 'ms') : 0,
        }))
      : [];

    const isDisconnected = asset?.metrics?.connection?.value === AssetConnectionStatus.NOCOMM;

    return {
      data: {
        ...asset,
        site,
        metrics: metrics,
        alarms: alarms?.length ? alarms.sort(sorter(sortMetric, sortDirection)) : undefined,
      },
      error,
      isLoading: isLoading || isAssetsLoading,
      dataUpdatedAt,
      isFetching,
      isDisconnected,
    };
  }, [
    asset,
    error,
    getSiteById,
    isAssetsLoading,
    isLoading,
    rtData,
    sortMetric,
    sortDirection,
    dataUpdatedAt,
    isFetching,
    assetDataAssignments,
    eventManagementData,
  ]);
};